import type { Geometry } from "geojson";
import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import type { ItinerarySegment } from "@mountayaapp/types/api/api";

import type { ItinerarySliceState } from "./itinerary";
import { mapSegments } from "./itinerary";

/**
 * segmentEditModeSchema is how a segment's geometry is authored: `directions`
 * routes between vias server-side, `linestring` is a freehand drawn line. It
 * matches the wire SegmentMode. Zod-first because the value is shared with the
 * edit-begin tool input and validated when an agent starts an edit.
 */
export const segmentEditModeSchema = z.enum(["directions", "linestring"]);

/**
 * SegmentEditMode is how a segment's geometry is authored, derived from
 * segmentEditModeSchema.
 */
export type SegmentEditMode = z.infer<typeof segmentEditModeSchema>;

/**
 * segmentViaPromptSchema is the open ask-the-user prompt for a clicked handle of
 * the segment being edited: which handle (`index` into the current draft
 * coordinates), where it sits (`lngLat`), and the container pixel the popup
 * anchors at (`point`, projected by the tool that opens it — the same
 * lngLat-plus-pixel pair the right-click context menu carries, so both popups
 * anchor the same way). Zod-first because the value is shared with the prompt-open
 * tool input and validated when the tool opens it; a null slot (no prompt) lives
 * outside the schema as the absence of a value.
 */
export const segmentViaPromptSchema = z.object({
  index: z.number().int().min(0),
  lngLat: z.tuple([z.number(), z.number()]),
  point: z.tuple([z.number(), z.number()]),
});

/**
 * SegmentViaPrompt is the open handle prompt, derived from
 * segmentViaPromptSchema. The slot holds this or null (no prompt open).
 */
export type SegmentViaPrompt = z.infer<typeof segmentViaPromptSchema>;

/**
 * DraftSegmentParams seed the synthetic ItinerarySegment a from-scratch edit
 * surfaces in the sidenav and the elevation panel before any route resolves: id
 * + itinerary scope (so swap-by-id holds), the authoring mode the chain will
 * commit in, and the draft name + color the row renders with.
 */
export interface DraftSegmentParams {
  draftColor: string | null;
  draftName: string | null;
  itineraryId: string;
  mode: SegmentEditMode;
  segmentId: string;
}

/**
 * synthesizeDraftSegment builds the placeholder ItinerarySegment a from-scratch
 * edit surfaces while no route has resolved yet. The id matches the edit's
 * segmentId so the chart and sidenav swap-by-id finds an entry to render; the
 * name + color + mode read out of the draft so the row reads with its real
 * label and hue from the first paint. The group is null — a segment joins a group
 * once it is part of the persisted chain, never while it is still being drawn.
 * Every other field is the neutral seed the
 * schema's non-null requirements need until the route's metrics overwrite them.
 * Geometry stays null — the editor's draft holds the in-flight vertices until
 * the routed line resolves into liveSegment via buildLiveSegment.
 */
export const synthesizeDraftSegment = (params: DraftSegmentParams): ItinerarySegment => ({
  altitude_max: null,
  altitude_min: null,
  ascent: null,
  color: params.draftColor,
  created_at: new Date(0),
  created_by: { id: "" },
  descent: null,
  description: null,
  distance: null,
  duration: null,
  geojson: null,
  gradient_max: null,
  gradient_min: null,
  group_id: null,
  id: params.segmentId,
  itinerary_id: params.itineraryId,
  mode: params.mode,
  name: params.draftName ?? "",
  position: 0,
  revision: 0,
  slope_info: [],
  speed: null,
  start_at: null,
  steps: [],
  surface_info: [],
  trail_difficulty_info: [],
  waytype_info: [],
});

/**
 * segmentInsertAnchorSchema places a from-scratch segment somewhere OTHER than the
 * tail of the chain: `afterSegmentId` names the segment the new one lands after,
 * and null means it lands at the head, ahead of the current first segment. The
 * field is required rather than optional so "at the head" and "no anchor at all"
 * stay distinguishable — the absence of the whole anchor is what marks a plain
 * append. Zod-first because it rides the segment editBegin tool input.
 */
export const segmentInsertAnchorSchema = z.object({
  afterSegmentId: z.string().nullable(),
});

/**
 * SegmentInsertAnchor places a from-scratch segment inside the chain rather than at
 * its tail, derived from {@link segmentInsertAnchorSchema}.
 */
export type SegmentInsertAnchor = z.infer<typeof segmentInsertAnchorSchema>;

/**
 * segmentEditBeginSchema is the input that starts a segment geometry edit: the
 * segment being edited, the authoring mode, the optional draft name + color the
 * synthetic live segment carries until a persisted entry exists for the
 * segment, and the engine arguments the map seam needs to arm the right
 * per-mode engine (the activity slug a directions edit routes for, the pinned
 * boundaries the chain locks to its neighbours, the seeded vias the editable
 * handles start from). A from-scratch edit opened from a seam between two
 * rows also carries the insert anchor that says where in the chain it lands, so the
 * commit writes an insert rather than an append. Zod-first because it is shared
 * with the segment editBegin tool and validated when an agent (or the UI) opens an
 * edit; the draft geometry and its history are not part of this input — they
 * accrue in the slice as the edit proceeds.
 */
export const segmentEditBeginSchema = z.object({
  activitySlug: z.string().optional(),
  draftColor: z.string().optional(),
  draftName: z.string().optional(),
  insertAnchor: segmentInsertAnchorSchema.optional(),
  mode: segmentEditModeSchema,
  pinnedEnd: z.tuple([z.number(), z.number()]).optional(),
  pinnedStart: z.tuple([z.number(), z.number()]).optional(),
  seedVias: z.array(z.tuple([z.number(), z.number()])).optional(),
  segmentId: z.string(),
});

/**
 * SegmentEditBegin is the segment + mode an edit starts with, derived from
 * segmentEditBeginSchema.
 */
export type SegmentEditBegin = z.infer<typeof segmentEditBeginSchema>;

/**
 * SegmentDraftSnapshot is one undo/redo history entry: a draft geometry paired with the
 * routed liveSegment that geometry resolved to when the entry was captured. Pairing the two
 * is what lets undo/redo restore the elevation profile in lockstep with the geometry — the
 * elevation lives on liveSegment, not on the raw draft, so a history that stored geometry
 * alone left the profile frozen on the pre-undo route until an async re-route happened to
 * land. Both fields are held by reference (the objects already exist on the session), so a
 * snapshot costs nothing to keep.
 */
export interface SegmentDraftSnapshot {
  draft: Geometry | null;
  liveSegment: ItinerarySegment | null;
}

/**
 * SegmentEditSession is the active segment geometry edit: the segment being
 * edited, the authoring mode, the current draft geometry (null when the edit
 * starts empty — e.g. a from-scratch directions segment with no vias yet), the
 * undo/redo history as past/future stacks of SegmentDraftSnapshot (each pairs a draft
 * geometry with the liveSegment it routed to), and the live segment the elevation panel
 * reads while the edit is open. A pushed draft change moves the prior snapshot (the prior
 * draft AND its liveSegment, including the empty null seed, so undo can return to the
 * starting state) onto `past` and clears `future`; undo moves the current snapshot to
 * `future` and restores the top of `past` — draft AND liveSegment together, so the elevation
 * profile tracks the restored geometry synchronously instead of waiting on an async
 * re-route; redo does the reverse. The map adapter re-seeds TerraDraw from `draft` whenever
 * it changes (the seam the editor and the agent both drive), so undo/redo
 * expose the geometry to re-apply by writing it back to `draft`. The draft is
 * a GeoJSON Geometry (the single source the route preview and the persisted
 * GeometryCollection are built from); it is not Zod-validated here for the
 * same reason itinerary geometry is cast at the openapi boundary — GeoJSON is
 * structurally large and trusted in-process. `liveSegment` carries an
 * in-memory ItinerarySegment projected from the latest routed response
 * (distance, ascent/descent, surface/way-type/slope analysis + the
 * freshly-routed geometry), so the elevation chart and the characteristics
 * row reflect the in-flight edit instead of the persisted state; null while
 * no route resolves (a linestring edit, a from-scratch directions edit before
 * two vias land, between commits).
 */
export interface SegmentEditSession {
  /**
   * activitySlug is the activity a directions edit routes for (the itinerary's,
   * every segment shares it). Carried on the session so the map seam can arm a
   * directions engine without the caller re-deriving the slug; null for a
   * linestring edit that needs no router.
   */
  activitySlug: string | null;
  draft: Geometry | null;
  /**
   * draftColor is the color the synthetic live segment renders in until a
   * persisted entry overwrites it. Carried for from-scratch edits so the
   * sidenav row and elevation chart pick up the appended segment's hue before
   * the first route resolves.
   */
  draftColor: string | null;
  /**
   * draftName is the human label the synthetic live segment carries until a
   * persisted entry exists. The auto-create flow seeds a friendly name; an
   * existing-segment edit leaves this null so the persisted name takes over.
   */
  draftName: string | null;
  future: SegmentDraftSnapshot[];
  /**
   * insertAnchor places a from-scratch segment inside the chain instead of at its
   * tail — the seam between two rows opens its draw with this set, and the commit
   * reads it to write an insert rather than an append. Null for every other edit:
   * an existing-segment reshape and a tail append both leave the chain's shape to
   * the ordinary update/create path.
   */
  insertAnchor: SegmentInsertAnchor | null;
  liveSegment: ItinerarySegment | null;
  mode: SegmentEditMode;
  past: SegmentDraftSnapshot[];
  /**
   * pinnedEnd is the end coordinate an inserted segment locks to the NEXT segment's
   * start (the editor renders it as an immovable seed handle, the mirror of
   * pinnedStart). Set only for an insert, which is the one edit with a successor to
   * meet: pinning it means the drawn line joins the following segment exactly and
   * the backend re-seats nothing. Null for an append and for a reshape, whose tail
   * stays the user's to drag.
   */
  pinnedEnd: [number, number] | null;
  /**
   * pinnedStart is the start coordinate the chain locks to the previous segment's
   * end (the editor renders it as an immovable seed handle). Null when starting a
   * new itinerary's first segment, and when inserting at the head of the chain.
   */
  pinnedStart: [number, number] | null;
  /**
   * seedVias are the editable handle coordinates the engine starts from (a
   * directions edit's vias or a linestring edit's line vertices). Empty when
   * the edit opens with no pre-seeded handles.
   */
  seedVias: [number, number][];
  segmentId: string;
}

/**
 * SegmentEditSliceState is the active segment geometry edit session and the
 * elevation-model dirty tick.
 */
export interface SegmentEditSliceState {
  /**
   * editDirtyTick increments on every mutation that changes the elevation
   * model's inputs but may not change the array identity React's memo path
   * depends on (rapid undo/redo, throttled live-segment writes, cancel
   * restoring the saved geometry, commit clearing the session). Consumers
   * subscribe to the tick from a memo's deps so the compute re-runs on every
   * dirty event, regardless of reference stability.
   */
  editDirtyTick: number;
  /**
   * segmentEditSession is the active segment geometry edit (null while nothing
   * is being authored). Mutually exclusive with waypointEditSession by
   * tool-layer policy.
   *
   * It is deliberately NOT one generic edit-session type shared with
   * waypointEditSession: the two model genuinely different concerns. A segment
   * session carries routing state (the draft geometry, the past/future draft
   * stacks, the live routed segment the elevation panel reads, the pinned start
   * + seed vias the append-only chain locks to); a waypoint session carries the
   * authoring intent, the saved `original` an update restores, and the
   * enrichment snapshot a `poi-pick` restores on close. Forcing them into one
   * shape would replace clear per-family fields with a union the readers would
   * have to narrow on every access. The parallel that IS shared lives one layer
   * down on the map engines (see `EditEngine`) and one layer up in the tool-bus
   * teardown (`teardownActiveEditSession`), where the behavior, not the data
   * shape, is the thing worth unifying.
   */
  segmentEditSession: SegmentEditSession | null;
  /**
   * segmentViaPrompt is the open ask-the-user prompt for a clicked handle of the
   * segment being edited (null = none open). It sits BESIDE the session rather
   * than inside it so opening and dismissing a prompt never rewrites the session
   * object — the elevation panel, the sidenav rows, and the map seam all subscribe
   * to that, and none of them care about the prompt. Cleared whenever a session
   * begins, commits, or cancels, so a prompt can never outlive the edit it targets.
   */
  segmentViaPrompt: SegmentViaPrompt | null;
}

/**
 * SegmentEditSliceActions are the segment authoring session lifecycle plus the
 * draft history, live-segment, and dirty-tick setters, reached only through the
 * tool bus. The relocated removeSegment lives here because removing a segment
 * also tears down the authoring session targeting it — it belongs with the
 * session it tears down.
 */
export interface SegmentEditSliceActions {
  beginSegmentEdit: (begin: SegmentEditBegin) => void;
  /**
   * bumpEditDirtyTick increments editDirtyTick by one. The seven edit-cycle
   * actions (beginSegmentEdit, pushSegmentDraft, undoSegmentDraft,
   * redoSegmentDraft, setSegmentEditLiveSegment, cancelSegmentEdit,
   * commitSegmentEdit) already bump the tick atomically inside their own
   * set() call; this public action is the opt-in seam for any future code
   * path that mutates the elevation model's inputs without going through
   * those seven, so the chart re-syncs without modifying the core actions.
   */
  bumpEditDirtyTick: () => void;
  cancelSegmentEdit: () => void;
  commitSegmentEdit: () => void;
  pushSegmentDraft: (geometry: Geometry) => void;
  redoSegmentDraft: () => void;
  /**
   * removeSegment drops a segment from the active itinerary, AND closes any edit
   * session whose segment was just removed in the same set() — the live slot
   * would otherwise hold a projection of a deleted entry, and the panel's inner
   * segments memo would append that projection as a tail ghost. It lives on the
   * segment-edit slice because the session teardown is the reason the action is
   * more than a plain itinerary patch.
   */
  removeSegment: (segmentId: string) => void;
  /**
   * recolorSegmentEditLive recolors the active segment edit in place: it patches the
   * session's draftColor AND the resolved liveSegment's color, so the elevation panel
   * (which plots liveSegment) tracks a mid-edit recolor and a re-route keeps the new
   * hue. A no-op when no segment session is active.
   */
  recolorSegmentEditLive: (color: string) => void;
  setSegmentEditDraft: (geometry: Geometry | null) => void;
  setSegmentEditLiveSegment: (segment: ItinerarySegment | null) => void;
  /**
   * setSegmentViaPrompt opens (a value) or dismisses (null) the ask-the-user
   * prompt for a clicked handle. Opening is refused when no segment session is
   * armed — a prompt targets a draft that must exist — so a stale emit after a
   * commit lands nowhere; dismissing always applies.
   */
  setSegmentViaPrompt: (prompt: SegmentViaPrompt | null) => void;
  undoSegmentDraft: () => void;
}

/**
 * seedViasEqual reports whether two seed-via lists are value-equal coordinate by
 * coordinate. beginSegmentEdit compares an incoming re-arm's seed against the
 * armed session's with it: the co-pilot re-proposing NEW vias on the open edit
 * re-seeds (the new proposal supersedes the in-flight draft), while a same-seed
 * re-arm (clicking the already-armed draft row) stays an idempotent no-op that
 * keeps the live elevation profile.
 */
const seedViasEqual = (a: readonly [number, number][], b: readonly [number, number][]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((via, index) => {
    const other = b[index];

    return other !== undefined && via[0] === other[0] && via[1] === other[1];
  });
};

/**
 * pinnedPointsEqual compares two optional pinned boundary coordinates by value
 * (null-safe). A pin is stable for a given segment, so it rarely differs across a
 * re-arm, but both ends are compared alongside the vias so the seed check is
 * complete.
 */
const pinnedPointsEqual = (
  a: readonly [number, number] | null,
  b: readonly [number, number] | null,
): boolean =>
  a === null || b === null ? a === null && b === null : a[0] === b[0] && a[1] === b[1];

/**
 * segmentEditInitialState seeds no active segment edit session and a zero dirty
 * tick.
 */
export const segmentEditInitialState: SegmentEditSliceState = {
  editDirtyTick: 0,
  segmentEditSession: null,
  segmentViaPrompt: null,
};

/**
 * createSegmentEditSlice builds the segment authoring session lifecycle plus the
 * draft history, live-segment, and dirty-tick setters. The seven edit-cycle
 * actions bump editDirtyTick atomically so the elevation model recomputes;
 * beginSegmentEdit clears the itinerary selection and projects a synthetic live
 * segment for a from-scratch edit. Every draft action no-ops when no segment
 * session is active.
 */
export const createSegmentEditSlice: StateCreator<
  SegmentEditSliceState &
    SegmentEditSliceActions &
    Pick<ItinerarySliceState, "activeItinerary" | "selection">,
  [],
  [],
  SegmentEditSliceActions
> = (set) => ({
  beginSegmentEdit: (begin) =>
    set((state) => {
      // Re-arming the exact same segment + mode WITH an unchanged seed is idempotent
      // (guardEditBegin permits it as a no-op): rebuilding the session here would reset
      // liveSegment to the synthetic stub and blank the live elevation profile mid-edit
      // (e.g. clicking the already-armed draft row re-dispatches editBegin with the
      // segment's own seed), so absorb the same-seed re-arm as a true no-op — the map
      // engine hook keys on the unchanged session and stays armed with its resolved live
      // segment. A CHANGED seed (the co-pilot re-proposing new vias on the open edit)
      // falls through so the new proposal supersedes the in-flight draft: fresh draft,
      // reset undo/redo stacks, re-seeded handles. The seed is value-compared so a
      // re-dispatch carrying an equal-but-fresh via array still no-ops.
      const active = state.segmentEditSession;
      if (
        active?.segmentId === begin.segmentId &&
        active?.mode === begin.mode &&
        seedViasEqual(active.seedVias, begin.seedVias ?? []) &&
        pinnedPointsEqual(active.pinnedStart, begin.pinnedStart ?? null) &&
        pinnedPointsEqual(active.pinnedEnd, begin.pinnedEnd ?? null)
      ) {
        return {};
      }

      // A from-scratch edit (the segment id is not in the persisted chain yet)
      // projects a synthetic live segment up front so the sidenav row and the
      // elevation panel surface the in-flight segment immediately — the route
      // preview, when it resolves, replaces this stub with the projected one.
      const persisted = state.activeItinerary?.segments?.find(
        (segment) => segment.id === begin.segmentId,
      );
      const liveSegment: ItinerarySegment | null = persisted
        ? null
        : synthesizeDraftSegment({
            draftColor: begin.draftColor ?? null,
            draftName: begin.draftName ?? null,
            itineraryId: state.activeItinerary?.id ?? "",
            mode: begin.mode,
            segmentId: begin.segmentId,
          });

      // Clear any prior selection: the user's focus is on the segment being
      // edited, not on whichever entity was selected before. A lingering
      // selection drives the segment-focused emphasis paint, which dims every
      // other segment of the itinerary while the edit is in flight. Dropping
      // the selection lets the emphasis fall back to the itinerary-wide mode
      // so the existing chain stays at full opacity.
      return {
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: {
          activitySlug: begin.activitySlug ?? null,
          draft: null,
          draftColor: begin.draftColor ?? null,
          draftName: begin.draftName ?? null,
          future: [],
          insertAnchor: begin.insertAnchor ?? null,
          liveSegment,
          mode: begin.mode,
          past: [],
          pinnedEnd: begin.pinnedEnd ?? null,
          pinnedStart: begin.pinnedStart ?? null,
          seedVias: begin.seedVias ?? [],
          segmentId: begin.segmentId,
        },
        // A fresh session supersedes any prompt the previous one left open — its
        // index pointed into a draft that is gone.
        segmentViaPrompt: null,
        selection: null,
      };
    }),

  bumpEditDirtyTick: () => set((state) => ({ editDirtyTick: state.editDirtyTick + 1 })),

  // Closing a session drops any open handle prompt with it: the prompt names an
  // index into a draft that no longer exists.
  cancelSegmentEdit: () =>
    set((state) => ({
      editDirtyTick: state.editDirtyTick + 1,
      segmentEditSession: null,
      segmentViaPrompt: null,
    })),

  commitSegmentEdit: () =>
    set((state) => ({
      editDirtyTick: state.editDirtyTick + 1,
      segmentEditSession: null,
      segmentViaPrompt: null,
    })),

  pushSegmentDraft: (geometry) =>
    set((state) => {
      if (!state.segmentEditSession) {
        return {};
      }

      const { draft, liveSegment, past } = state.segmentEditSession;

      return {
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: {
          ...state.segmentEditSession,
          draft: geometry,
          future: [],
          // Snapshot the prior draft WITH the liveSegment it routed to, so an undo restores
          // the elevation alongside the geometry. The new draft keeps the current liveSegment
          // until its own route resolves and overwrites it.
          past: [...past, { draft, liveSegment }],
        },
      };
    }),

  recolorSegmentEditLive: (color) =>
    set((state) => {
      const session = state.segmentEditSession;
      if (!session) {
        return {};
      }

      // Recolor patches the live segment AND every history snapshot's liveSegment, so a
      // mid-edit recolor stays applied across undo/redo (the snapshots would otherwise
      // restore the pre-recolor hue).
      const recolor = (segment: ItinerarySegment | null): ItinerarySegment | null =>
        segment ? { ...segment, color } : null;

      return {
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: {
          ...session,
          draftColor: color,
          future: session.future.map((snapshot) => ({
            ...snapshot,
            liveSegment: recolor(snapshot.liveSegment),
          })),
          liveSegment: recolor(session.liveSegment),
          past: session.past.map((snapshot) => ({
            ...snapshot,
            liveSegment: recolor(snapshot.liveSegment),
          })),
        },
      };
    }),

  redoSegmentDraft: () =>
    set((state) => {
      const session = state.segmentEditSession;
      if (!session) {
        return {};
      }

      if (session.future.length === 0) {
        return {};
      }

      const { draft, future, liveSegment, past } = session;
      const [next, ...remaining] = future;
      if (next === undefined) {
        return {};
      }

      return {
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: {
          ...session,
          draft: next.draft,
          future: remaining,
          liveSegment: next.liveSegment,
          past: [...past, { draft, liveSegment }],
        },
      };
    }),

  removeSegment: (segmentId) =>
    set((state) => {
      // The store closes any edit session whose segment was just removed in
      // the same set() — the live slot would otherwise hold a projection of
      // a deleted entry, and the panel's inner segments memo would append
      // that projection as a tail ghost. Co-locating the invariant next to
      // the action means every caller (the bus remove tool, a future
      // drag-to-delete) inherits it without having to remember an ordering.
      const activeItinerary = mapSegments(state.activeItinerary, (segments) =>
        segments.filter((segment) => segment.id !== segmentId),
      );
      if (state.segmentEditSession?.segmentId !== segmentId) {
        return { activeItinerary };
      }

      return {
        activeItinerary,
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: null,
      };
    }),

  setSegmentEditDraft: (geometry) =>
    set((state) =>
      state.segmentEditSession
        ? { segmentEditSession: { ...state.segmentEditSession, draft: geometry } }
        : {},
    ),

  setSegmentEditLiveSegment: (segment) =>
    set((state) =>
      state.segmentEditSession
        ? {
            editDirtyTick: state.editDirtyTick + 1,
            segmentEditSession: { ...state.segmentEditSession, liveSegment: segment },
          }
        : {},
    ),

  setSegmentViaPrompt: (segmentViaPrompt) =>
    set((state) =>
      // A prompt names an index into the armed draft, so it opens only while a
      // session is armed; a dismiss (null) always applies, so a torn-down session
      // can still clear a slot the teardown order left behind.
      segmentViaPrompt !== null && !state.segmentEditSession ? {} : { segmentViaPrompt },
    ),

  undoSegmentDraft: () =>
    set((state) => {
      const session = state.segmentEditSession;
      if (!session) {
        return {};
      }

      if (session.past.length === 0) {
        return {};
      }

      const { draft, future, liveSegment, past } = session;
      const previous = past[past.length - 1];
      if (previous === undefined) {
        return {};
      }

      return {
        editDirtyTick: state.editDirtyTick + 1,
        segmentEditSession: {
          ...session,
          // Restore the draft AND its routed liveSegment together so the elevation profile
          // reflects the restored geometry at once; the current pair moves to `future`.
          draft: previous.draft,
          future: [{ draft, liveSegment }, ...future],
          liveSegment: previous.liveSegment,
          past: past.slice(0, -1),
        },
      };
    }),
});
